import React, { useState } from 'react';
import { Box, Button, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, Collapse } from '@mui/material';
import logo from '../../../Assets/Images/V2/logo.svg';
import Menu from './Menu';
import { To, useNavigate } from 'react-router-dom';
import menuIconWhite from '../../../Assets/Images/V2/Menu Icon-white.svg';
import linkedinIcon from '../../../Assets/Images/linkedinIcon.svg';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { trackCta, decorateCrossDomainUrl, getDestinationDomain } from '../../../Utils/analytics';
import type { AppSurface } from '../../../Utils/analytics';
import { useOptionalHeroView } from '../../../Utils/ContextHelper/HeroViewContext';
import { useBookDemo } from '../../../Utils/ContextHelper/BookDemoContext';
import { ASSESSMENT_BUSINESS_PATH, ASSESSMENT_CANDIDATE_PATH, FREE_TRIAL_URL } from '../../../Utils/urls';

// Define navigation items with labels and routes
const navItems = [
	{ label: 'Product', path: '/product/6d-assessment' },
	{ label: 'About Us', path: '/about-us' },
	{ label: 'Trust Center', path: '/trust-center' }
];

const TopNav = () => {
	const iconData = [
		// { img: TwitterIcon, link: '' },
		// { img: LanguageIcon, link: '' },
		// { img: UpworkIcon, link: '' },
		// { img: SocialIcon, link: '' },
		{ img: linkedinIcon, link: 'https://www.linkedin.com/company/simsola-ai/about/?viewAsMember=true', alt: 'LinkedIn' }
	];
	const [mobileOpen, setMobileOpen] = useState(false);
	const [mobileProductOpen, setMobileProductOpen] = useState(false);

	const navigate = useNavigate();
	const heroView = useOptionalHeroView();
	const { openBookDemo } = useBookDemo();
	// On the homepage's MyCoach view the nav CTA becomes a demo request.
	const isMyCoachView = heroView?.view === 'my_coach';

	// Toggle the mobile drawer open/close
	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	// Handle navigation and close the drawer
	const handleNavItemClick = (path: To) => {
		navigate(path);
		setMobileOpen(false);
		setMobileProductOpen(false);
	};

	const handleProductPageClick = (path: string, ctaLabel: string, appSurface: AppSurface) => {
		trackCta('product page clicked', { cta_label: ctaLabel, product: 'assessment', app_surface: appSurface, location: 'nav' });
		handleNavItemClick(path);
	};

	const handleFreeTrialClick = () => {
		trackCta('free trial clicked', {
			cta_label: 'Start a Free Trial',
			product: 'assessment',
			app_surface: 'business',
			location: 'nav',
			destination_domain: getDestinationDomain(FREE_TRIAL_URL)
		});
		window.open(decorateCrossDomainUrl(FREE_TRIAL_URL), '_blank', 'noopener,noreferrer');
	};

	const handleNavCtaClick = () => {
		if (isMyCoachView) {
			openBookDemo({ cta_label: 'Book a Demo', location: 'nav', hero_view: 'my_coach' });
		} else {
			handleFreeTrialClick();
		}
	};

	return (
		<Box sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.13)', backgroundColor: '#FBFBFB' }}>
			{/* Top Navigation Bar */}
			<Box
				className="container"
				sx={{
					padding: '17px 0',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '5px',
					position: 'relative',
					overflow: 'hidden',
					width: '100%',
					'@media (max-width: 480px)': {
						padding: '17px 10px'
					}
				}}>
				{/* Logo on the left */}
				<Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', flexShrink: 0, zIndex: 1 }}>
					<img src={logo} alt="Simsola logo" className="logoImage" />
				</Box>
				{/* Desktop menu */}
				<Box
					sx={{
						display: { xs: 'none', md: 'block' },
						position: 'absolute',
						left: '50%',
						transform: 'translateX(-50%)',
						width: 'fit-content',
						maxWidth: 'calc(100% - 360px)',
						overflow: 'hidden',
						zIndex: 0
					}}>
					<Menu />
				</Box>
				{/* Mobile menu icon on the right */}
				<Box sx={{ display: { xs: 'block', md: 'none' }, flexShrink: 0 }}>
					<IconButton onClick={handleDrawerToggle} edge="end" sx={{ marginRight: 0 }} aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}>
						{/* <MenuIcon /> */}
						<Box
							sx={{
								width: '40px',
								height: '40px',
								border: ' 1.213px solid rgba(0, 115, 222, 0.23)',
								borderRadius: '50%',
								boxShadow: '0px 0px 0px 2.883px rgba(1, 133, 255, 0.28)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: mobileOpen ? '#0073DE' : '#fff'
							}}>
							{mobileOpen ? (
								<img src={menuIconWhite} alt="" width={24} height={24} style={{ width: '24px', height: '24px' }} />
							) : (
								<img src={menuIconWhite} alt="" width={24} height={24} style={{ width: '24px', height: '24px', filter: 'invert(100%)' }} />
							)}
						</Box>
					</IconButton>
				</Box>

				{/* Desktop "Book a Call" button */}
				<Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0, zIndex: 1 }}>
					<Button
						variant="contained"
						sx={{
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: 400,
							fontFamily: 'DM Mono',
							lineHeight: '150%',
							letterSpacing: '-0.32px',
							padding: '11px 17px',
							whiteSpace: 'nowrap',
							backgroundColor: '#0073DE',
							'&:hover': { backgroundColor: '#005CB8' }
						}}
						onClick={handleNavCtaClick}
					>
						{isMyCoachView ? 'Book a Demo' : 'Start a Free Trial'}
					</Button>
				</Box>
			</Box>

			{/* Mobile Drawer */}
			<Drawer
				anchor="left"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				sx={{
					'& .MuiDrawer-paper': { width: '100%', marginTop: '74px', padding: '32px' }
				}}>
				<List>
					{navItems.map((item, index) => {
						if (item.label === 'Product') {
							return (
								<React.Fragment key={index}>
									<ListItem disablePadding>
										<ListItemButton onClick={() => setMobileProductOpen(v => !v)}>
											<ListItemText
												primaryTypographyProps={{
													sx: { color: '#000', fontFamily: 'DM Sans', fontSize: '24px', fontWeight: '600' }
												}}
												primary="Product"
											/>

											<IconButton
												edge="end"
												aria-label={mobileProductOpen ? 'Collapse product navigation' : 'Expand product navigation'}
												onClick={e => {
													e.stopPropagation();
													setMobileProductOpen(v => !v);
												}}
											>
												{mobileProductOpen ? <ExpandLess /> : <ExpandMore />}
											</IconButton>
										</ListItemButton>
									</ListItem>

									<Collapse in={mobileProductOpen} timeout="auto" unmountOnExit>
										<List component="div" disablePadding sx={{ pl: 4 }}>
											<ListItem disablePadding>
												<ListItemButton onClick={() => handleProductPageClick(ASSESSMENT_BUSINESS_PATH, 'Assessment Service for Businesses', 'business')}>
													<ListItemText
														primaryTypographyProps={{
															sx: { color: '#000', fontFamily: 'DM Sans', fontSize: '18px', fontWeight: '500' }
														}}
														primary="Assessment Service for Businesses"
													/>
												</ListItemButton>
											</ListItem>
											<ListItem disablePadding>
												<ListItemButton onClick={() => handleProductPageClick(ASSESSMENT_CANDIDATE_PATH, 'Assessment Service for Candidates', 'candidate')}>
													<ListItemText
														primaryTypographyProps={{
															sx: { color: '#000', fontFamily: 'DM Sans', fontSize: '18px', fontWeight: '500' }
														}}
														primary="Assessment Service for Candidates"
													/>
												</ListItemButton>
											</ListItem>
											<ListItem disablePadding>
												<ListItemButton onClick={() => handleNavItemClick('/product/6d-assessment')}>
													<ListItemText
														primaryTypographyProps={{
															sx: { color: '#000', fontFamily: 'DM Sans', fontSize: '18px', fontWeight: '500' }
														}}
														primary="MyCoach (L&D)"
													/>
												</ListItemButton>
											</ListItem>
										</List>
									</Collapse>
								</React.Fragment>
							);
						}

						return (
							<ListItem key={index} disablePadding>
								<ListItemButton onClick={() => handleNavItemClick(item.path)}>
									<ListItemText
										primaryTypographyProps={{
											sx: { color: '#000', fontFamily: 'DM Sans', fontSize: '24px', fontWeight: '600' }
										}}
										primary={item.label}
									/>
								</ListItemButton>
							</ListItem>
						);
					})}
				</List>
				<Divider />
				{/* "Book a Call" button inside the Drawer */}
				<Box sx={{ padding: 2 }}>
					<Button
						// fullWidth
						variant="contained"
						sx={{
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: 400,
							fontFamily: 'DM Mono',
							lineHeight: '150%',
							letterSpacing: '-0.32px',
							padding: '11px 17px',
							backgroundColor: '#0073DE',
							'&:hover': { backgroundColor: '#005CB8' }
						}}
						onClick={() => {
							handleNavCtaClick();
							setMobileOpen(false);
						}}
					>
						{isMyCoachView ? 'Book a Demo' : 'Start a Free Trial'}
					</Button>
				</Box>
				<Box sx={{ padding: '16px' }}>
					<Box sx={{ display: 'flex', gap: '8px' }}>
						{iconData.map((items, index) => {
							return (
								<>
									<Box
										key={index}
										onClick={() => {
											if (items.link) {
												window.open(items.link, '_blank', 'noopener,noreferrer');
											}
										}}
										sx={{
											// backgroundColor: 'white',
											padding: '10px',
											border: '1px solid rgba(0, 0, 0, 0.13)',
											borderRadius: '8px',
											width: '40px',
											height: '40px',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center'
										}}>
										<img src={items.img} alt={items?.alt} width={20} height={20} />
									</Box>
								</>
							);
						})}
					</Box>
				</Box>
			</Drawer>
		</Box>
	);
};

export default TopNav;
